import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { errMsg, nzTime } from "@/lib/hsUtil";
import { Shell, TypePicker, DetailsForm, SignedInCard } from "./SiteParts";
import { Induction } from "./InductionSteps";

const TOKEN_KEY = "lwco_site_token";
const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } };

export default function SiteSignIn() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("home");
  const [done, setDone] = useState("");
  const [form, setForm] = useState({ type: "Visitor", name: "", company: "", phone: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/site/${code}`, { headers: { "X-Site-Token": getToken() } });
      setData(r.data);
      if (r.data.me) setForm(f => ({ ...f, type: r.data.me.type }));
    } catch (e) { setLoadErr(errMsg(e, "Site not found.")); }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  async function run(fn) {
    setErr(""); setBusy(true);
    try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }
  const signIn = (extra = {}) => run(async () => {
    const body = data.me ? { token: getToken(), type: form.type, ...extra } : { token: "", ...form, ...extra };
    const { data: r } = await api.post(`/site/${code}/sign-in`, body);
    if (r.token) localStorage.setItem(TOKEN_KEY, r.token);
    setDone(`Signed in at ${nzTime(r.signed_in_at)}. Have a safe day.`);
  });
  const signOut = () => run(async () => {
    await api.post(`/site/${code}/sign-out`, { token: getToken() });
    setDone("Signed out. Thanks, see you next time.");
  });
  const fromDetails = () => {
    if (form.name.trim().length < 2 || !form.company.trim() || form.phone.replace(/\D/g, "").length < 6) {
      setErr("Enter your name, company and a phone number."); return;
    }
    setErr(""); setStep("induct");
  };
  const notMe = () => { localStorage.removeItem(TOKEN_KEY); setData(d => ({ ...d, me: null, status: { signed_in: false, needs_induction: true } })); };

  if (loadErr) return (
    <Shell><div className="card space-y-3" data-testid="site-error"><div>{loadErr}</div>
      <Link to="/site" className="btn-ghost inline-block" data-testid="site-enter-code-link">Enter a 6 digit site code</Link></div></Shell>
  );
  if (!data) return <Shell><div className="opacity-70">Loading…</div></Shell>;
  if (done) return <Shell project={data.project}><div className="card text-lg text-silver" data-testid="site-done">{done}</div></Shell>;

  const { me, status } = data;
  let body;
  if (status.signed_in) body = <SignedInCard status={status} onSignOut={signOut} busy={busy}/>;
  else if (step === "induct") body = <Induction data={data} code={code} defaultName={me?.name || form.name} busy={busy}
    onCancel={() => setStep("home")} onFinish={signIn}/>;
  else if (me) body = (
    <div className="card space-y-3" data-testid="site-welcome-card">
      <div className="text-lg" data-testid="site-welcome-name">Welcome{status.needs_induction ? "" : " back"}, {me.name}</div>
      {me.company && <div className="text-sm opacity-70">{me.company}</div>}
      <TypePicker value={form.type} onChange={t => setForm(f => ({ ...f, type: t }))}/>
      {status.needs_induction
        ? <button className="btn-silver w-full py-3" onClick={() => setStep("induct")} data-testid="site-start-induction-btn">Start site induction</button>
        : <button className="btn-silver w-full py-4 text-lg" disabled={busy} onClick={() => signIn()} data-testid="site-one-tap-btn">Sign in</button>}
      {status.needs_induction && <div className="text-xs opacity-60" data-testid="site-induction-required">Induction needed: first visit, or more than 30 days since your last visit.</div>}
      {!me.hub_user && <button className="text-xs underline opacity-70" onClick={notMe} data-testid="site-not-me-btn">Not you?</button>}
    </div>
  );
  else body = <DetailsForm form={form} setForm={setForm} onContinue={fromDetails} busy={busy}/>;

  return (
    <Shell project={data.project}>
      {body}
      {err && <div className="text-red-400 text-sm" data-testid="site-error-msg">{err}</div>}
    </Shell>
  );
}
